/**
 * Peek a moment (spec 11 card 2, §3.4 row 2; motion 7). After 300 ms of hover or keyboard focus on a
 * match line (or its faces), or a touch long-press, a popover plays a muted 20-second clip at
 * `match.at` with captions, then shows who said it and "Open here". Nothing changes on the page.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { ArrowUpRight, Pause, Play } from "lucide-react";
import { Face } from "../shared/Face";
import { clock } from "../../lib/format";
import { recipes } from "../../motion";
import type { Match, MeetingSummary, Person } from "../../types";

const CLIP = 20;      // seconds
const LONG_PRESS = 500;
const CLOSE_GRACE = 160; // ms to move the pointer from the line into the popover

const short = (iso: string) => new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric" });

/** The muted clip: Vimeo's player (no controls of its own), stopped after 20 s. Progress is written to the DOM. */
function Clip({ vimeoId, at }: { vimeoId: string; at: number }) {
  const host = useRef<HTMLDivElement>(null);
  const fill = useRef<HTMLSpanElement>(null);
  const time = useRef<HTMLSpanElement>(null);
  const player = useRef<Player | null>(null);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const p = new Player(el, { id: Number(vimeoId), muted: true, autoplay: true, controls: false, texttrack: "en", title: false, byline: false, portrait: false, dnt: true, autopause: false } as ConstructorParameters<typeof Player>[1]);
    player.current = p;
    const show = (s: number) => {
      const e = Math.min(CLIP, Math.max(0, s - at));
      if (fill.current) fill.current.style.transform = `scaleX(${e / CLIP})`;
      if (time.current) time.current.textContent = `${clock(e)} / ${clock(CLIP)}`;
      return e;
    };
    p.on("timeupdate", ({ seconds }: { seconds: number }) => {
      if (show(seconds) >= CLIP) { void p.pause().catch(() => {}); void p.setCurrentTime(at).catch(() => {}); }
    });
    p.on("play", () => setPlaying(true));
    p.on("pause", () => setPlaying(false));
    void p.ready().then(() => p.setCurrentTime(at)).then(() => p.play()).catch(() => {});
    show(at);
    return () => { player.current = null; void p.destroy().catch(() => {}); };
  }, [vimeoId, at]);

  const toggle = () => { const p = player.current; if (p) void (playing ? p.pause() : p.play()).catch(() => {}); };
  return (
    <div className="peek__video">
      <div ref={host} className="peek__player" />
      <div className="peek__controls">
        <button className="peek__play" aria-label={playing ? "Pause" : "Play"} onClick={toggle}>
          {playing ? <Pause size={10} fill="currentColor" strokeWidth={0} /> : <Play size={10} fill="currentColor" strokeWidth={0} />}
        </button>
        <span className="peek__track"><span ref={fill} className="peek__fill" /></span>
        <span ref={time} className="peek__time">{`${clock(0)} / ${clock(CLIP)}`}</span>
      </div>
    </div>
  );
}

export function PeekCard({ m, meeting, speaker, href, onEnter, onLeave }: {
  m: Match & { at: number };
  meeting: MeetingSummary;
  speaker: Person | null;
  href: string;
  onEnter: () => void;
  onLeave: () => void;
}) {
  // Opens below the line; flips above it when the window has no room underneath.
  const card = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = card.current;
    if (el && el.getBoundingClientRect().bottom > innerHeight - 8 && el.parentElement!.getBoundingClientRect().top > el.offsetHeight + 16) el.dataset.side = "top";
  }, []);
  const sub = [speaker?.ward != null ? `Ward ${speaker.ward}` : null, `${meeting.body}, ${short(meeting.startsAt)}`, clock(m.at)].filter(Boolean).join(" · ");
  return (
    <div ref={card} className="peek" role="dialog" aria-label={`${m.label} at ${clock(m.at)}`} onPointerEnter={onEnter} onPointerLeave={onLeave}>
      {meeting.vimeoId ? <Clip vimeoId={meeting.vimeoId} at={m.at} /> : <div className="peek__video" />}
      <div className="peek__who">
        {speaker && <Face name={speaker.name} photoUrl={speaker.photoUrl} size={28} />}
        <span className="peek__text">
          {speaker && <span className="peek__name">{speaker.name}</span>}
          <span className="peek__sub">{sub}</span>
        </span>
        <a className="peek__open" href={href}>Open here<ArrowUpRight size={12} /></a>
      </div>
      {m.summary && <p className="peek__summary">{m.summary}</p>}
    </div>
  );
}

/**
 * Hover/focus intent and long-press for one match line. Only one peek is open at a time
 * (opening one closes the others through the "peek:open" event).
 */
export function usePeek(key: string, enabled: boolean) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pressed = useRef(false);
  useEffect(() => {
    const other = (e: Event) => { if ((e as CustomEvent<string>).detail !== key) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    addEventListener("peek:open", other);
    addEventListener("keydown", esc);
    return () => { removeEventListener("peek:open", other); removeEventListener("keydown", esc); clearTimeout(timer.current); };
  }, [key]);
  const show = () => { setOpen(true); dispatchEvent(new CustomEvent("peek:open", { detail: key })); };
  const intent = (ms: number) => { clearTimeout(timer.current); timer.current = setTimeout(show, ms); };
  const cancel = (grace = CLOSE_GRACE) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), grace); };
  const stay = () => clearTimeout(timer.current);
  if (!enabled) return { open: false, lineProps: {}, cardProps: { onEnter: stay, onLeave: () => cancel() } };
  return {
    open,
    lineProps: {
      onPointerEnter: (e: React.PointerEvent) => { if (e.pointerType === "mouse") intent(recipes.peek.hoverIntentMs); },
      onPointerLeave: (e: React.PointerEvent) => { if (e.pointerType === "mouse") cancel(); },
      onFocus: () => intent(recipes.peek.hoverIntentMs),
      onBlur: (e: React.FocusEvent) => { if (!e.relatedTarget || !(e.currentTarget.parentElement?.contains(e.relatedTarget as Node))) cancel(0); },
      onTouchStart: () => { pressed.current = false; clearTimeout(timer.current); timer.current = setTimeout(() => { pressed.current = true; show(); }, LONG_PRESS); },
      onTouchEnd: () => { if (!pressed.current) clearTimeout(timer.current); },
      onTouchMove: () => { if (!pressed.current) clearTimeout(timer.current); },
      onClick: (e: React.MouseEvent) => { if (pressed.current) { e.preventDefault(); pressed.current = false; } },
      onContextMenu: (e: React.MouseEvent) => { if (pressed.current) e.preventDefault(); },
    },
    cardProps: { onEnter: stay, onLeave: () => cancel() },
  };
}
